import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ASSIGNABLE_ROLES,
  RoleSelect,
  UserSelect,
  useCustomRoles,
  useTeams,
  useWorkspaceMembers,
} from '@/entities/collab';
import {
  useDeleteDocumentRule,
  useDocumentAccess,
  useRemoveDocumentMember,
  useSetDocumentDenial,
  useSetDocumentMember,
  useSetDocumentRule,
  type WorkspaceDocument,
} from '@/entities/document';
import { FormError } from '@/shared/forms';
import { Button, Combobox, Dialog, ErrorState, IconButton, Icons, LoadingState } from '@/shared/ui';

import styles from './DocumentAccess.module.css';

/**
 * Who may do what with one note: explicit participants (authoritative), rules for teams and
 * workspace roles, and denials. The same concepts and precedence as a database's access panel.
 */
export function DocumentAccessDialog({
  document,
  isTeam,
  onClose,
}: {
  document: WorkspaceDocument;
  isTeam: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const roleName = (role: string) => (t as unknown as (k: string) => string)(`roles.${role}`);
  const access = useDocumentAccess(document.id);
  const members = useWorkspaceMembers(document.workspace_id);
  const teams = useTeams(document.workspace_id, isTeam);
  const customRoles = useCustomRoles(document.workspace_id, isTeam);
  const setMember = useSetDocumentMember(document.id);
  const removeMember = useRemoveDocumentMember(document.id);
  const setDenial = useSetDocumentDenial(document.id);
  const setRule = useSetDocumentRule(document.id);
  const deleteRule = useDeleteDocumentRule(document.id);
  const [person, setPerson] = useState('');
  const [role, setRole] = useState('editor');
  const [subject, setSubject] = useState('');
  const [subjectRole, setSubjectRole] = useState('member');
  const error =
    setMember.error ?? removeMember.error ?? setDenial.error ?? setRule.error ?? deleteRule.error;
  const data = access.data;
  const assignable = (data?.assignable_roles ?? ASSIGNABLE_ROLES) as typeof ASSIGNABLE_ROLES;
  const taken = new Set([
    data?.owner_user_id ?? '',
    ...(data?.members.map((m) => m.user_id) ?? []),
    ...(data?.denials.map((d) => d.user_id) ?? []),
  ]);
  const subjects = [
    ...(teams.data ?? []).map((team) => ({
      value: `team:${team.id}`,
      label: t('documents.access.teamSubject', { name: team.name }),
    })),
    ...ASSIGNABLE_ROLES.map((value) => ({
      value: `role:${value}`,
      label: t('documents.access.roleSubject', { name: t(`roles.${value}`) }),
    })),
    ...(customRoles.data ?? []).map((custom) => ({
      value: `custom:${custom.id}`,
      label: t('documents.access.roleSubject', { name: custom.name }),
    })),
  ];
  return (
    <Dialog title={t('documents.access.title')} size="lg" onClose={onClose}>
      {access.isPending && <LoadingState />}
      {access.isError && <ErrorState error={access.error} />}
      {data && (
        <div className={styles.body} data-testid="document-access">
          <FormError error={error} />
          <p className={styles.note}>{t('documents.access.owner', { name: data.owner_name })}</p>
          <section aria-label={t('documents.access.people')}>
            <h3 className={styles.heading}>{t('documents.access.people')}</h3>
            <ul className={styles.list}>
              {data.members.map((member) => (
                <li key={member.user_id} className={styles.row}>
                  <span className={styles.name}>{member.display_name}</span>
                  <RoleSelect
                    label={t('documents.access.roleOf', { name: member.display_name })}
                    value={member.role as (typeof ASSIGNABLE_ROLES)[number]}
                    roles={assignable}
                    onChange={(next) => {
                      setMember.mutate({ userId: member.user_id, role: next });
                    }}
                  />
                  <IconButton
                    label={t('documents.access.remove', { name: member.display_name })}
                    icon={<Icons.close />}
                    onClick={() => {
                      removeMember.mutate(member.user_id);
                    }}
                  />
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDenial.mutate({ userId: member.user_id, denied: true });
                    }}
                  >
                    {t('documents.access.deny')}
                  </Button>
                </li>
              ))}
            </ul>
            <form
              className={styles.add}
              onSubmit={(event) => {
                event.preventDefault();
                if (!person) return;
                setMember.mutate(
                  { userId: person, role },
                  {
                    onSuccess: () => {
                      setPerson('');
                    },
                  },
                );
              }}
            >
              <UserSelect
                label={t('documents.access.person')}
                users={members.data ?? []}
                value={person}
                exclude={taken}
                onChange={setPerson}
              />
              <RoleSelect
                label={t('documents.access.role')}
                value={role as (typeof ASSIGNABLE_ROLES)[number]}
                roles={assignable}
                onChange={setRole}
              />
              <Button type="submit" variant="secondary" disabled={!person}>
                {t('documents.access.add')}
              </Button>
            </form>
          </section>
          {isTeam && (
            <section aria-label={t('documents.access.rules')}>
              <h3 className={styles.heading}>{t('documents.access.rules')}</h3>
              <ul className={styles.list}>
                {data.rules.map((rule) => (
                  <li key={rule.id} className={styles.row}>
                    <span className={styles.name}>
                      {rule.subject_kind === 'team'
                        ? t('documents.access.teamSubject', { name: rule.subject_name })
                        : t('documents.access.roleSubject', {
                            name:
                              rule.subject_kind === 'role'
                                ? roleName(rule.subject_name)
                                : rule.subject_name,
                          })}
                    </span>
                    <span>{roleName(rule.role)}</span>
                    <IconButton
                      label={t('documents.access.removeRule')}
                      icon={<Icons.close />}
                      onClick={() => {
                        deleteRule.mutate(rule.id);
                      }}
                    />
                  </li>
                ))}
              </ul>
              <form
                className={styles.add}
                onSubmit={(event) => {
                  event.preventDefault();
                  const [kind, id] = subject.split(':');
                  if (!kind || !id) return;
                  setRule.mutate(
                    kind === 'team'
                      ? { subject_kind: 'team', team_id: id, role: subjectRole }
                      : kind === 'custom'
                        ? { subject_kind: 'custom_role', custom_role_id: id, role: subjectRole }
                        : { subject_kind: 'role', subject_role: id, role: subjectRole },
                    {
                      onSuccess: () => {
                        setSubject('');
                      },
                    },
                  );
                }}
              >
                <Combobox
                  label={t('documents.access.subject')}
                  value={subject}
                  options={subjects}
                  searchPlaceholder={t('common.search')}
                  emptyText={t('common.noResults')}
                  onChange={setSubject}
                />
                <RoleSelect
                  label={t('documents.access.role')}
                  value={subjectRole as (typeof ASSIGNABLE_ROLES)[number]}
                  roles={assignable}
                  onChange={setSubjectRole}
                />
                <Button type="submit" variant="secondary" disabled={!subject}>
                  {t('documents.access.add')}
                </Button>
              </form>
            </section>
          )}
          {data.denials.length > 0 && (
            <section aria-label={t('documents.access.denied')}>
              <h3 className={styles.heading}>{t('documents.access.denied')}</h3>
              <ul className={styles.list}>
                {data.denials.map((denial) => (
                  <li key={denial.user_id} className={styles.row}>
                    <span className={styles.name}>{denial.display_name}</span>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setDenial.mutate({ userId: denial.user_id, denied: false });
                      }}
                    >
                      {t('documents.access.undeny')}
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </Dialog>
  );
}
